import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { F, R, MONO_FEAT, withAlpha, mr } from '../styles/theme';
import { buildTaxLots, summarizeLots } from '../utils/taxLots';
import { ALL_COINS, availableHoldingCoins, buildReleaseAgenda } from '../utils/releaseAgenda';
import { lotFreeMs } from '../utils/holdingPeriod';
import ReleaseAgenda from '../components/red/ReleaseAgenda';
import { dropScamDustTxs } from '../utils/txView';
import { useTheme } from '../context/ThemeContext';
import { useUserPlan } from '../context/UserPlanContext';
import { useProfile } from '../context/ProfileContext';
import { useMarketData } from '../context/MarketDataContext';
import { COIN_MAP } from '../utils/coingecko';
import { buildReport, listReportYears } from '../utils/reportEngine';
import { rulesFor } from '../utils/taxRules';
import { calcPortfolio } from '../utils/portfolioCalc';
import { fmt, fmtC, fmtEur } from '../utils/format';
import { useT } from '../i18n/useT';
import { useCurrency } from '../context/CurrencyContext';
import CoinImage from '../components/CoinImage';
import Private from '../components/Private';
import { useMaskText } from '../context/PrivacyContext';
import { CoinSelectButton, CoinPickerSheet } from '../components/red/CoinSelect';
import RedButton from '../components/red/RedButton';
import BackButton from '../components/BackButton';
import { Icon, Icons } from '../components/red/Icons';
import SectionHeader from '../components/red/SectionHeader';
import SelectSheet from '../components/red/SelectSheet';
import ExportSheet from '../components/red/ExportSheet';
import { Segmented } from '../components/red/Editorial';
import { exportYearReport } from '../utils/exportService';

// ── PAYMENT MODAL ─────────────────────────────────────────────────────────────
// Named export — wird von ProfileScreen importiert. Beibehalten, aber rot-getoenter
// Icon-Container (Memory-Regel-Verstoss) durch neutralen T.glass + roten Stroke ersetzt.


// ── DRILLDOWN ─────────────────────────────────────────────────────────────────
// Sub-View innerhalb der Berichte-Seite. Zeigt fuer einen Coin im gewaehlten
// Jahr ALLE steuer-relevanten Events mit FIFO-Matching aufgeklappt.
// Der Kunde sieht damit "warum die Zahl die Zahl ist".

function FifoMatchList({ T, soldLots }) {
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  if (!soldLots || soldLots.length === 0) {
    return (
      <div style={{ fontSize: 11, color: T.mute, fontStyle: 'italic' }}>
        {t('tax_no_acquisition')}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {soldLots.map((lot, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          padding: '8px 0',
          borderTop: i === 0 ? 'none' : `1px solid ${withAlpha(T.text, 0.04)}`,
          fontSize: 12,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ ...MONO_FEAT, color: T.text, fontWeight: 700, letterSpacing: '-0.01em' }}>{lot.datum}</span>
            <span style={{ ...MONO_FEAT, color: T.sub }}><Private>{fmtC(lot.use)}</Private></span>
          </span>
          <span style={{ ...MONO_FEAT, color: T.mute }}>
            @ <Private>{fmtEur(convert(lot.stueck), locale)}</Private> {symbol}
          </span>
        </div>
      ))}
    </div>
  );
}

// Status-Pill für die Haltefrist — Wording konform mit feedback_no_tax_naming.md:
// "ÜBER 1-JAHRES-FRIST" / "IN HALTEFRIST" statt "STEUERFREI / STEUERPFLICHTIG".
function HoldStatus({ T, stpfl, hf }) {
  const { t } = useT();
  const gain = T.gain || T.green;
  const loss = T.loss || T.red;
  const color = stpfl ? loss : gain;
  const label = stpfl ? t('tax_in_holding') : t('tax_over_year');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 6,
      fontSize: 10, fontWeight: 700, letterSpacing: '-0.005em',
      color, 
    }}>
      {label}
      {hf != null && (
        <span style={{ ...MONO_FEAT, color: withAlpha(color, 0.75), letterSpacing: '0.04em' }}>
          · {hf} {t('days_short')}
        </span>
      )}
    </span>
  );
}

function EventCard({ T, isDE, ev }) {
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const { convert, symbol, locale } = useCurrency();
  const gain = T.gain || T.green;
  const loss = T.loss || T.red;
  const isLoss = ev.gv != null && ev.gv < 0;
  const sign = (ev.gv || 0) >= 0 ? '+' : '−';
  const showAk = !ev.costBasisUnknown && ev.ak > 0;

  const typLabel = ev.typ === 'verkauf' ? t('sell')
                 : ev.typ === 'swap'    ? t('tax_swap')
                 : ev.typ === 'loss'    ? t('tax_loss')
                 : ev.typ;
  const typColor = ev.typ === 'verkauf' ? loss
                 : ev.typ === 'swap'    ? (T.isDark ? '#9C8FD4' : '#8E7CC3')
                 : ev.typ === 'loss'    ? loss
                 : T.sub;

  const mengeStr = ev.typ === 'swap'
    ? `${fmtC(ev.mengeOut || 0)} ${ev.coinOut} → ${fmtC(ev.mengeIn || 0)} ${ev.coinIn}`
    : `${fmtC(ev.menge || 0)} ${ev.coin || ''}`;

  const hasFifo = (ev.typ === 'verkauf' || ev.typ === 'swap' || ev.typ === 'loss');

  return (
    <div style={{
      padding: '22px 0',
      borderBottom: `1px solid ${T.line}`,
    }}>
      {/* Eyebrow-Row: Datum + Typ-Pill links | Hold-Status rechts */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ ...MONO_FEAT, fontSize: 12.5, color: T.text, fontWeight: 700, letterSpacing: '-0.01em' }}>{ev.datum}</span>
          <span style={{
            fontSize: 12.5, fontWeight: 700, color: typColor,
            letterSpacing: '-0.005em',
          }}>
            {typLabel}
          </span>
        </div>
        {/* DE: Haltefrist-Status immer. AT (Ø-Kosten): nur bei Altvermögen
            sinnvoll — Neuvermögen kennt keine Frist. Übrige: keine Bewertung. */}
        {(ev.costBasisMethod === 'average' ? !!ev.altvermoegen : isDE) && ev.gv != null && (
          <HoldStatus T={T} stpfl={ev.stpfl} hf={ev.hf} />
        )}
      </div>

      {/* Headline: Menge */}
      <div style={{
        fontFamily: F.display, fontSize: 17, fontWeight: 700,
        letterSpacing: '-0.02em', color: T.text, marginBottom: 16,
      }}>
        <Private>{mengeStr}</Private>
      </div>

      {/* Zahlen-Strip — Desktop: sharp Metrics mit vertikalen Trennern.
          Mobile: Ergebnis als Anker + kompakte Erlös/Anschaffung-Subzeile,
          damit die Beträge auf schmalen Geräten nicht umbrechen. */}
      {isWide ? (
        <div style={{
          display: 'flex', gap: 0, marginBottom: hasFifo ? 16 : 0, flexWrap: 'wrap',
        }}>
          <Metric T={T} label={t('tax_proceeds')} value={<Private>{`${fmtEur(convert(ev.gross || 0), locale)} ${symbol}`}</Private>} dim={!(ev.gross > 0)} />
          {showAk && <Metric T={T} label={t('tax_acquisition')} value={<Private>{`${fmtEur(convert(ev.ak), locale)} ${symbol}`}</Private>} />}
          {ev.fee > 0 && <Metric T={T} label={t('tax_fee')} value={<Private>{`${fmtEur(convert(ev.fee), locale)} ${symbol}`}</Private>} />}
          {ev.gv != null && (
            <Metric T={T} label={t('tax_result')}
              value={<>{sign}<Private>{`${fmtEur(convert(Math.abs(ev.gv)), locale)} ${symbol}`}</Private></>}
              color={isLoss ? loss : gain}
              strong
            />
          )}
        </div>
      ) : (
        <div style={{ marginBottom: hasFifo ? 16 : 0 }}>
          {ev.gv != null && (
            <>
              <div style={{
                fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
                color: T.mute, marginBottom: 5,
              }}>{t('tax_result')}</div>
              <div style={{
                ...MONO_FEAT, fontFamily: F.display,
                fontSize: 24, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1,
                color: isLoss ? loss : gain, marginBottom: 10,
              }}>
                {sign}<Private>{fmtEur(convert(Math.abs(ev.gv)), locale)}</Private> {symbol}
              </div>
            </>
          )}
          {/* Erlös · Anschaffung · Gebühr — kompakt, Label gedämpft. */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '4px 16px',
            fontFamily: F.family, fontSize: 11.5, color: T.sub, ...MONO_FEAT,
          }}>
            <span><span style={{ color: T.mute }}>{t('tax_proceeds')} </span><Private>{fmtEur(convert(ev.gross || 0), locale)}</Private> {symbol}</span>
            {showAk && <span><span style={{ color: T.mute }}>{t('tax_acquisition')} </span><Private>{fmtEur(convert(ev.ak), locale)}</Private> {symbol}</span>}
            {ev.fee > 0 && <span><span style={{ color: T.mute }}>{t('tax_fee')} </span><Private>{fmtEur(convert(ev.fee), locale)}</Private> {symbol}</span>}
          </div>
        </div>
      )}

      {/* FIFO-Matching — eigene Hairline-Section */}
      {hasFifo && (
        <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4,
          }}>
            <span style={{
              fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
              color: T.mute, 
            }}>
              {t('tax_fifo_match')}
            </span>
            <div style={{ flex: 1, height: 1, background: T.line }} />
          </div>
          <FifoMatchList T={T} soldLots={ev.soldLots} />
        </div>
      )}

      {/* Warnings inline — kein rotes/gelbes BG-Tinting, nur Schrift + Hairline */}
      {(ev.costBasisUnknown || ev.incomplete || ev.fallbackUsed) && (
        <div style={{
          marginTop: 14, paddingTop: 12,
          borderTop: `1px solid ${T.line}`,
          fontSize: 11.5, color: T.sub, lineHeight: 1.55,
        }}>
          {ev.costBasisUnknown && <div>· {t('tax_warn_cost_basis')}</div>}
          {ev.incomplete && <div>· {t('tax_warn_incomplete')}</div>}
          {ev.fallbackUsed && <div>· {t('tax_warn_fallback')}</div>}
        </div>
      )}
    </div>
  );
}

function Metric({ T, label, value, color, strong, dim }) {
  return (
    <div style={{
      flex: '1 1 0', minWidth: 100,
      borderLeft: `1px solid ${T.line}`,
      paddingLeft: 14, paddingRight: 14,
    }}>
      <div style={{
        fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
        color: T.mute, marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        ...MONO_FEAT, fontFamily: F.display,
        fontSize: strong ? 17 : 15,
        fontWeight: 700,
        color: color || (dim ? T.mute : T.text),
        letterSpacing: '-0.02em', lineHeight: 1,
      }}>
        {value}
      </div>
    </div>
  );
}

function DrilldownView({ T, asset, year, isDE, marketData, onBack, hideBack = false }) {
  const { t } = useT();
  const { isWide } = useBreakpoint();
  const { convert, symbol, locale } = useCurrency();
  const gain = T.gain || T.green;
  const loss = T.loss || T.red;

  return (
    <div style={{ padding: '8px 0 100px', color: T.text }}>
      {/* Header — Editorial sharp, Coin-Name als Wordmark mit Brand-Punkt.
          hideBack: in der nativen App ist das Drilldown eine eigene Ionic-Route
          mit nativem Swipe-back → der Pfeil entfällt (redundant). */}
      <div style={{ padding: '8px 18px 22px', display: 'flex', alignItems: 'center', gap: 14 }}>
        {!hideBack && <BackButton onBack={onBack} title={t('back')} size={36} />}
        <CoinImage sym={asset.sym} marketData={marketData} size={38} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 style={{
            fontFamily: F.display, fontSize: 30, fontWeight: 700,
            letterSpacing: '-0.035em', margin: 0, lineHeight: 1,
            color: T.text,
            display: 'inline-flex', alignItems: 'baseline',
          }}>
            <span>{asset.name || asset.sym}</span>
          </h1>
          <div style={{
            ...MONO_FEAT, fontSize: 11, color: T.mute, marginTop: 6,
            letterSpacing: '-0.005em', fontWeight: 700,
          }}>
            {t('tax_year')} {year} · {asset.eventsCount} {asset.eventsCount === 1 ? t('tax_event') : t('tax_events')}
          </div>
        </div>
      </div>

      {/* Summary — Desktop: 3-Spalten Editorial mit vertikalen Trennern.
          Mobile: eigenes Stack-Layout (Ergebnis als Headline + Label/Wert-Zeilen),
          damit die großen Beträge nicht umbrechen. */}
      <div style={{ padding: '0 18px 18px' }}>
        {isWide ? (
          <div style={{
            borderTop: `1px solid ${T.line}`, paddingTop: 20,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
          }}>
            <SummaryCell T={T} label={t('tax_paid_out')}
              value={<Private>{`${fmtEur(convert(asset.grossProceeds), locale)} ${symbol}`}</Private>} />
            <SummaryCell T={T} label={t('tax_result_fifo')}
              value={<>{asset.realizedPnL >= 0 ? '+' : '−'}<Private>{`${fmtEur(convert(Math.abs(asset.realizedPnL)), locale)} ${symbol}`}</Private></>}
              color={asset.realizedPnL >= 0 ? gain : loss}
              withBorder
            />
          </div>
        ) : (
          <div style={{
            borderTop: `1px solid ${T.line}`, paddingTop: 18,
          }}>
            {/* Headline: realisiertes Ergebnis (Kern eines Veräußerungs-Reports) */}
            <div style={{
              fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
              color: T.mute, marginBottom: 9,
            }}>
              {t('tax_result_fifo')}
            </div>
            <div style={{
              ...MONO_FEAT, fontFamily: F.display,
              fontSize: 34, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1,
              color: asset.realizedPnL >= 0 ? gain : loss,
            }}>
              {asset.realizedPnL >= 0 ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(asset.realizedPnL)), locale)}</Private> {symbol}
            </div>
            {/* Sekundär: Ausbezahlt als Label/Wert-Zeile (Heute·Live entfällt —
                steht bereits im Dashboard). */}
            <div style={{ marginTop: 16 }}>
              <ReportRow T={T} label={t('tax_paid_out')}
                value={<Private>{`${fmtEur(convert(asset.grossProceeds), locale)} ${symbol}`}</Private>} />
            </div>
          </div>
        )}
      </div>

      {/* Section-Eyebrow für die Event-Liste */}
      <div style={{ padding: '0 18px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          fontFamily: F.family, fontSize: 11, fontWeight: 700,
          letterSpacing: '-0.005em', color: T.text,
        }}>{t('tax_events')}</span>
        <div style={{ flex: 1, height: 1, background: T.line }} />
        <span style={{ ...MONO_FEAT, fontSize: 11, color: T.mute, fontWeight: 600 }}>
          {asset.eventsCount}
        </span>
      </div>

      {/* Events — verdichtet pro (Tag + Coin-Paar + Steuer-Status), damit
          Binance-Trades die in Sekundenintervalle gesplittet sind, als eine
          Karte erscheinen. Einzelne FIFO-Matches bleiben im Detail-Block. */}
      <div style={{ padding: '0 18px' }}>
        {mergeEventsByDay(asset.events).map((ev, i) => (
          <EventCard key={i} T={T} isDE={isDE} ev={ev} />
        ))}
      </div>
    </div>
  );
}

// Mobile-Report-Zeile: Label links (klein, Caps), Wert rechtsbündig (groß, mono).
// Der Wert bekommt die volle Restbreite → keine Umbrüche bei großen Beträgen.
function ReportRow({ T, label, value, sub }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14,
      padding: '13px 0 2px', borderTop: `1px solid ${T.line}`,
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '-0.005em',
        color: T.mute, flexShrink: 0,
      }}>{label}</span>
      <div style={{ textAlign: 'right', minWidth: 0 }}>
        <div style={{
          ...MONO_FEAT, fontFamily: F.display,
          fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05,
          color: T.text,
        }}>{value}</div>
        {sub && (
          <div style={{ fontSize: 10.5, color: T.sub, marginTop: 5, ...MONO_FEAT }}>{sub}</div>
        )}
      </div>
    </div>
  );
}

function SummaryCell({ T, label, value, color, sub, withBorder }) {
  return (
    <div style={{
      paddingLeft: withBorder ? 24 : 0,
      borderLeft: withBorder ? `1px solid ${T.line}` : 'none',
    }}>
      <div style={{
        fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
        color: T.mute, marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        ...MONO_FEAT, fontFamily: F.display,
        fontSize: 26, fontWeight: 700,
        letterSpacing: '-0.035em', lineHeight: 1,
        color: color || T.text,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: T.sub, marginTop: 8, ...MONO_FEAT }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Fasst Events nach (datum, typ, coinOut, coinIn, stpfl) zusammen.
// Summiert Mengen/Erlös/Anschaffung/Gebühr/Ergebnis und konkateniert soldLots.
// Erhalt: Steuer-Bucket (stpfl) und Coin-Paar separat — damit FIFO-Beträge
// pro Bucket korrekt bleiben. Haltedauer wird als Range angezeigt.
function mergeEventsByDay(events) {
  const groups = new Map();
  for (const ev of events) {
    const key = [
      ev.datum,
      ev.typ,
      ev.typ === 'swap' ? (ev.coinOut || '') : (ev.coin || ''),
      ev.typ === 'swap' ? (ev.coinIn  || '') : '',
      ev.stpfl ? '1' : '0',
      ev.costBasisUnknown ? 'u' : 'k',
    ].join('|');
    const g = groups.get(key);
    if (!g) {
      groups.set(key, {
        datum: ev.datum,
        typ: ev.typ,
        coin: ev.coin, coinOut: ev.coinOut, coinIn: ev.coinIn,
        menge: ev.menge || 0,
        mengeOut: ev.mengeOut || 0,
        mengeIn: ev.mengeIn || 0,
        gross: ev.gross || 0,
        ak: ev.ak || 0,
        fee: ev.fee || 0,
        gv: ev.gv,
        hfMin: ev.hf, hfMax: ev.hf,
        stpfl: ev.stpfl,
        soldLots: [...(ev.soldLots || [])],
        costBasisUnknown: !!ev.costBasisUnknown,
        incomplete: !!ev.incomplete,
        fallbackUsed: !!ev.fallbackUsed,
        priceSource: ev.priceSource,
        _count: 1,
      });
    } else {
      g.menge    += ev.menge    || 0;
      g.mengeOut += ev.mengeOut || 0;
      g.mengeIn  += ev.mengeIn  || 0;
      g.gross    += ev.gross    || 0;
      g.ak       += ev.ak       || 0;
      g.fee      += ev.fee      || 0;
      g.gv = (g.gv == null || ev.gv == null) ? (g.gv ?? ev.gv) : g.gv + ev.gv;
      if (ev.hf != null) {
        g.hfMin = g.hfMin == null ? ev.hf : Math.min(g.hfMin, ev.hf);
        g.hfMax = g.hfMax == null ? ev.hf : Math.max(g.hfMax, ev.hf);
      }
      g.soldLots.push(...(ev.soldLots || []));
      g.incomplete = g.incomplete || !!ev.incomplete;
      g.fallbackUsed = g.fallbackUsed || !!ev.fallbackUsed;
      g._count += 1;
    }
  }
  // Range-Label für hf erzeugen (für EventCard-Anzeige)
  const out = [];
  for (const g of groups.values()) {
    g.hf = g.hfMin === g.hfMax ? g.hfMin : `${g.hfMin}-${g.hfMax}`;
    delete g.hfMin; delete g.hfMax;
    out.push(g);
  }
  // Chronologisch sortieren (ältestes oben? oder neuestes oben? — wie original)
  out.sort((a, b) => (a.datum > b.datum ? -1 : a.datum < b.datum ? 1 : 0));
  return out;
}

// ── Smart Check (Desktop) — nach source/red-screens-tax.jsx ─────────────────
// SectionHead chapter="DE" eyebrow="Smart Check"
// title="Wann erreicht deine Position die 1-Jahres-Frist?"
// + 3-Col Stats (Freigegeben% / Nächste Tranche / FIFO-Tranchen)
// + Statement-Card mit FullTimeline (Heute-Linie, Balken je Tranche,
//   Countdown in Tagen, grüner Tick bei „frei")
// + Disclaimer-Dokument mit roten Eck-Marken
//
// Wording-Regel (memory feedback_no_tax_naming.md):
//   "steuerfrei" → "frei (1-Jahres-Frist)"  /  "Haltefrist erreicht"
//   "steuerpflichtig" → "in Haltefrist"
const YEAR_MS_SC = 365 * 24 * 60 * 60 * 1000;

function fmtDateDE(d) {
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function coinAmtDE(n) {
  if (!isFinite(n)) return '–';
  const dec = n >= 1000 ? 2 : n >= 1 ? 4 : 6;
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: dec });
}

function FullTimeline({ lots, sym, T /* eslint-disable-line no-unused-vars */, priceEur /* eslint-disable-line no-unused-vars */ }) {
  // Clean Editorial: solider Bar, kein Outline/Glow. HEUTE-Marker als vertikale
  // Linie quer über alle Rows. „Gebunden" = sanftes Rot (kein Marken-Rot).
  const RED_SC = T.loss || T.red || '#C2564E';
  const GAIN = T.gain || T.green || '#39C46A';
  const now = +new Date();
  if (!lots || lots.length === 0) return null;
  const earliest = Math.min(...lots.map(l => +new Date(l.buyDate)));
  // Frei-Datum kanonisch aus holdingPeriod (Jahrestag + 1 Tag, BGB §188 II) —
  // identisch zur Engine/Agenda, nicht „rollend 365" (sonst 1-Tag-Versatz).
  const latestFree = Math.max(...lots.map(l => lotFreeMs(l.buyDate) ?? (+new Date(l.buyDate) + YEAR_MS_SC)));
  const end = Math.max(latestFree, now);
  const span = end - earliest || 1;
  const todayPct = ((now - earliest) / span) * 100;

  // Spalten-Layout — left Label-Col (150), middle Timeline-Flex, right Status (44)
  const LEFT_W = 150;
  const RIGHT_W = 44;

  return (
    <div style={{ position: 'relative', paddingTop: 28, paddingBottom: 36 }}>
      {/* HEUTE-Marker: Caps-Label oben + vertikale durchgezogene Linie quer
          durch alle Rows. Die Linie sitzt an der Today-Position innerhalb des
          Timeline-Cells (also rechnerisch verschoben um LEFT_W + gap). */}
      <div style={{
        position: 'absolute',
        top: 18, bottom: 24,
        left: `calc(${LEFT_W}px + 14px + ${todayPct}% * (100% - ${LEFT_W + RIGHT_W + 28}px) / 100%)`,
        width: 1, background: withAlpha(T.text, 0.18), zIndex: 2,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: 0,
        left: `calc(${LEFT_W}px + 14px + ${todayPct}% * (100% - ${LEFT_W + RIGHT_W + 28}px) / 100%)`,
        transform: 'translateX(-50%)',
        fontFamily: F.family, fontSize: 11.5, fontWeight: 700,
        letterSpacing: '-0.005em',
        color: T.sub, zIndex: 3,
        background: T.bg, padding: '0 6px', whiteSpace: 'nowrap',
      }}>HEUTE</div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {lots.map((lot, i) => {
          const buyMs = +new Date(lot.buyDate);
          const freeMs = lotFreeMs(lot.buyDate) ?? (buyMs + YEAR_MS_SC);
          const isFree = now >= freeMs;
          const startPct = ((buyMs - earliest) / span) * 100;
          const fullPct  = ((freeMs - earliest) / span) * 100 - startPct;
          const heldPct  = ((Math.min(now, freeMs) - earliest) / span) * 100 - startPct;
          const days = Math.max(0, Math.ceil((freeMs - now) / (24 * 3600 * 1000)));
          const barColor = isFree ? GAIN : RED_SC;

          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 14, minHeight: 34,
            }}>
              {/* Linke Spalte: Menge + Datum.
                  Sym als kleines Caps-Label statt direkt an die Zahl geklebt —
                  liest sich wie ein Tag, nicht wie weiterlaufender Text. */}
              <div style={{ width: LEFT_W, flexShrink: 0 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'baseline', gap: 8,
                  lineHeight: 1.15,
                }}>
                  <span style={{
                    fontFamily: F.display, fontSize: 13.5, fontWeight: 700,
                    letterSpacing: '-0.015em', color: T.text,
                  }}>
                    <Private>{coinAmtDE(lot.amount)}</Private>
                  </span>
                  <span style={{
                    fontFamily: F.family, fontSize: 10, fontWeight: 700,
                    letterSpacing: '-0.005em', 
                    color: T.mute,
                  }}>
                    {sym}
                  </span>
                </div>
                <div style={{
                  ...MONO_FEAT, fontSize: 12, color: T.sub, marginTop: 3,
                  fontWeight: 600, letterSpacing: '0.01em',
                }}>
                  {fmtDateDE(lot.buyDate)}
                </div>
              </div>

              {/* Mittel-Spalte: solider Bar (Pill, rounded) ohne Outline/Glow */}
              <div style={{ flex: 1, position: 'relative', height: 6 }}>
                <div style={{
                  position: 'absolute', top: 0,
                  left: `${startPct}%`,
                  width: `${heldPct}%`,
                  height: 6, borderRadius: mr(16),
                  background: barColor,
                }} />
                {/* Stichtag-Marker für noch-nicht-frei Lots */}
                {!isFree && (
                  <div style={{
                    position: 'absolute', top: -2,
                    left: `${startPct + fullPct}%`,
                    width: 2, height: 10,
                    background: GAIN, borderRadius: 1,
                  }} />
                )}
              </div>

              {/* Rechts: grüner Check (frei) oder Tage-Countdown (gebunden) */}
              <div style={{
                width: RIGHT_W, flexShrink: 0, textAlign: 'right',
                display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'center',
              }}>
                {isFree ? (
                  <span role="img" aria-label="frei" style={{ display: 'inline-flex' }}>
                    <Icon d={Icons.check} size={16} stroke={GAIN} sw={2.4} />
                  </span>
                ) : (
                  <span style={{
                    ...MONO_FEAT, color: RED_SC, fontWeight: 700, fontSize: 12.5,
                  }}>
                    {days}<span style={{ fontSize: 9, color: withAlpha(RED_SC, 0.7), marginLeft: 1 }}>T</span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom-Skala: Date-Range links/rechts, dezent */}
      <div style={{
        position: 'absolute', bottom: 0, left: LEFT_W + 14, right: RIGHT_W + 14,
        display: 'flex', justifyContent: 'space-between',
        ...MONO_FEAT, fontSize: 11.5, color: T.sub,
        fontWeight: 600, letterSpacing: '0.01em',
      }}>
        <span>{fmtDateDE(new Date(earliest))}</span>
        <span>{fmtDateDE(new Date(end))}</span>
      </div>
    </div>
  );
}

function SmartCheckScreen({ txs, marketData: marketDataProp, onSelectAsset, onOpenAlert }) {
  const { T, isDark } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  // Markt-Daten primär aus Context — Prop bleibt als Fallback.
  const { marketData: marketDataCtx } = useMarketData();
  const marketData = marketDataCtx && Object.keys(marketDataCtx).length ? marketDataCtx : (marketDataProp || {});

  // Scam-Dust (Address-Poisoning) aus den Haltefrist-Lots heraushalten — zentraler
  // Filter wie in WalletScreen/AssetDetail. Staking (HBAR) bleibt (nie _scamDust).
  const dustFreeTxs = useMemo(() => dropScamDustTxs(txs, marketData), [txs, marketData]);

  // Coins mit offenen Lots — zentral via availableHoldingCoins (Fiat/Dust/Preis-
  // Filter, größter Bestand zuerst). Früher hier 1:1 dupliziert.
  const availableCoins = useMemo(
    () => availableHoldingCoins(txs, dustFreeTxs, marketData),
    [txs, dustFreeTxs, marketData]
  );

  // Default = „Alle Coins"-Überblick (Freigabe-Agenda). Ein Coin → Einzel-Timeline.
  const [coin, setCoin] = useState(ALL_COINS);
  const [searchOpen, setSearchOpen] = useState(false);
  const isOverview = coin === ALL_COINS;

  // Falls der gewählte Einzel-Coin verschwindet → zurück auf den Überblick.
  useEffect(() => {
    if (!isOverview && availableCoins.length && !availableCoins.includes(coin)) {
      setCoin(ALL_COINS);
    }
  }, [availableCoins, coin, isOverview]);

  const effectiveCoin = isOverview
    ? ALL_COINS
    : (availableCoins.includes(coin) ? coin : (availableCoins[0] || coin));

  // Per-Coin-Daten nur im Einzel-Modus berechnen.
  const lots = useMemo(
    () => (isOverview ? [] : buildTaxLots(effectiveCoin, dustFreeTxs)),
    [isOverview, effectiveCoin, dustFreeTxs]
  );
  const sum = useMemo(() => summarizeLots(lots), [lots]);
  const freePct = sum.total > 0 ? (sum.free / sum.total) * 100 : 0;

  // Portfolio-Freigabe-Agenda für den Überblick-Modus.
  const agenda = useMemo(
    () => (isOverview ? buildReleaseAgenda({ coins: availableCoins, dustFreeTxs, marketData }) : null),
    [isOverview, availableCoins, dustFreeTxs, marketData]
  );

  // Live-Marktwert via Context: aktueller EUR-Preis pro Coin-Einheit (Einzel-Modus).
  const coinId = isOverview ? null : COIN_MAP[effectiveCoin]?.id;
  const priceEur = coinId ? marketData?.[coinId]?.price : null;
  const totalValueEur = typeof priceEur === 'number' && priceEur > 0 ? sum.total * priceEur : null;
  const freeValueEur  = typeof priceEur === 'number' && priceEur > 0 ? sum.free  * priceEur : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: T.text, fontFamily: F.family }}>

      {/* Kopf — Calm (wie Berichte): ruhiger Sub + Coin-Picker + ruhiger
          Detail-Button, Display-Headline, Hairline. Kein „02"-Nummern-Eyebrow. */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          {isOverview ? (
            <span style={{
              fontFamily: F.family, fontSize: 13, fontWeight: 500,
              letterSpacing: '-0.005em', color: T.sub,
            }}>{t('tax_holding_status')}</span>
          ) : (
            // Einzel-Coin (Detail): Zurück in den „Alle Coins"-Überblick.
            <button
              type="button"
              className="mo-row"
              onClick={() => setCoin(ALL_COINS)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 11px 6px 8px',
                background: withAlpha(T.text, 0.06), border: 'none', borderRadius: 999,
                color: T.text, cursor: 'pointer',
                fontFamily: F.family, fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg className="mo-lead" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={T.sub} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 6l-6 6 6 6" />
              </svg>
              {t('red_ag_all_coins')}
            </button>
          )}
          {availableCoins.length > 0 && (
            <CoinSelectButton
              coin={effectiveCoin}
              marketData={marketData}
              allLabel={t('red_ag_all_coins')}
              onOpen={() => setSearchOpen(true)}
            />
          )}
          <div style={{ flex: 1, height: 1, background: T.line }} />
          {onSelectAsset && !isOverview && availableCoins.length > 0 && (
            <button
              className="mo-row"
              onClick={() => onSelectAsset({
                sym: effectiveCoin,
                coinId: COIN_MAP[effectiveCoin]?.id,
                name: COIN_MAP[effectiveCoin]?.name || effectiveCoin,
              })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 16px',
                background: T.surface,
                border: `1px solid ${T.line}`,
                borderRadius: 12,
                color: T.text,
                boxShadow: isDark ? '0 8px 22px rgba(0,0,0,0.30)' : '0 8px 22px rgba(34,30,24,0.12)',
                fontFamily: F.family, fontWeight: 700, fontSize: 13.5,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform .12s',
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              {t('tax_open_detail')}
              <svg className="mo-chev" width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke={T.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </div>
        <div style={{ borderBottom: `1px solid ${T.line}`, paddingBottom: 16 }}>
          <h2 style={{
            fontFamily: F.display, fontSize: 49, fontWeight: 800,
            letterSpacing: '-0.046em', lineHeight: 0.98, margin: 0, color: T.text,
          }}>{isOverview ? t('red_ag_title') : t('tax_sc_title')}</h2>
          <p style={{
            margin: '14px 0 0', fontSize: 15, color: T.sub,
            lineHeight: 1.5, maxWidth: 680,
          }}>
            {isOverview ? t('red_ag_sub') : t('tax_sc_sub')}
          </p>
        </div>

      </div>

      <style>{`
        @keyframes scCoinSwap {
          0%   { opacity: 0; transform: scale(0.6); }
          55%  { opacity: 1; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes scFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scModalIn {
          from { opacity: 0; transform: scale(0.94) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {searchOpen && (
        <CoinPickerSheet
          coins={availableCoins}
          current={effectiveCoin}
          marketData={marketData}
          allOption
          allLabel={t('red_ag_all_coins')}
          onPick={(c) => { setCoin(c); setSearchOpen(false); }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Überblick (Alle Coins): Portfolio-Freigabe-Agenda. Klick auf eine Zeile
          → Einzel-Coin (setCoin) → die per-Coin-Timeline unten. */}
      {isOverview ? (
        <ReleaseAgenda
          agenda={agenda}
          onPickCoin={setCoin}
          marketData={marketData}
          onReminder={onOpenAlert ? () => onOpenAlert({ type: 'holding_period' }) : undefined}
        />
      ) : (
      <>
      {/* Haltefrist — flach (Container entfernt): Big % + Sub + Statement +
          Timeline + Legende sitzen direkt auf der warmen Seite. */}
      <div style={{ paddingTop: 4 }}>
        {/* Header: big % links | Sub-Zeile rechts */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 22, marginBottom: 8 }}>
          <div key={effectiveCoin + Math.round(freePct)} style={{
            fontFamily: F.display, fontSize: 64, fontWeight: 800,
            letterSpacing: '-0.05em', lineHeight: 0.9,
            color: T.gain || T.green,
            animation: 'scFadeIn 320ms ease-out',
          }}>
            {Math.round(freePct)}<span style={{ fontSize: 36, opacity: 0.7, marginLeft: 2 }}>%</span>
          </div>
          <div style={{
            fontFamily: F.family, fontSize: 13.5, color: T.sub,
            lineHeight: 1.45, paddingTop: 14,
          }}>
            {t('tax_pct_of_holdings', { coin })}<br/>{t('tax_over_1y_period')}
          </div>
          {totalValueEur != null && (
            <div style={{
              marginLeft: 'auto', textAlign: 'right',
              paddingTop: 14, minWidth: 120,
            }}>
              <div style={{
                fontFamily: F.family, fontSize: 11.5, fontWeight: 700,
                letterSpacing: '-0.005em', color: T.mute,
                marginBottom: 4,
              }}>{t('tax_market_value')}</div>
              <div style={{
                ...MONO_FEAT, fontFamily: F.display, fontSize: 22, fontWeight: 700,
                letterSpacing: '-0.03em', color: T.text, lineHeight: 1,
              }}><Private>{fmtEur(convert(totalValueEur), locale)}</Private> <span style={{ fontSize: 12, color: T.mute }}>{symbol}</span></div>
              <div style={{ fontSize: 10.5, color: T.sub, marginTop: 4 }}>
                {t('tax_current_live_price')}
              </div>
            </div>
          )}
        </div>

        {/* Statement: nächste Tranche */}
        <div style={{
          fontFamily: F.family, fontSize: 15, color: T.text,
          lineHeight: 1.45, marginBottom: 4,
        }}>
          {sum.daysToNextFree != null && sum.nextFreeLot ? (
            <>
              {t('tax_in_prefix')} <b>{t('tax_n_days', { n: sum.daysToNextFree })}</b> {t('tax_next_tranche_mid')}{' '}
              <b style={MONO_FEAT}><Private>{coinAmtDE(sum.nextFreeLot.amount)}</Private> {coin}</b> {t('tax_next_tranche_suffix')}
            </>
          ) : (
            <>{t('tax_all_tranches_free')}</>
          )}
        </div>

        <FullTimeline lots={lots} sym={coin} T={T} priceEur={priceEur} />

        {/* Legende: frei | gebunden */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 18,
          marginTop: 16, fontSize: 12, color: T.sub, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.gain || T.green }} />
            <span style={{ ...MONO_FEAT, color: T.text, fontWeight: 700 }}><Private>{coinAmtDE(sum.free)}</Private> {coin}</span>
            <span>{t('tax_free_legend')}{freeValueEur != null && (
              <span style={{ color: T.mute }}> · <Private>{fmtEur(convert(freeValueEur), locale)}</Private> {symbol}</span>
            )}</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.loss || T.red }} />
            <span style={{ ...MONO_FEAT, color: T.text, fontWeight: 700 }}><Private>{coinAmtDE(sum.taxable)}</Private> {coin}</span>
            <span>{t('tax_bound_legend')}{priceEur != null && (
              <span style={{ color: T.mute }}> · <Private>{fmtEur(convert(sum.taxable * priceEur), locale)}</Private> {symbol}</span>
            )}</span>
          </span>
        </div>
      </div>
      </>
      )}

      {/* Footer-Hinweis — flach, kein Dokument-Chrome (Erklärung №/Seite raus) */}
      <div style={{
        borderTop: `1px solid ${T.line}`, paddingTop: 22,
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 24, alignItems: 'start',
      }}>
        <h3 style={{
          fontFamily: F.display, fontSize: 22, fontWeight: 700,
          letterSpacing: '-0.03em', lineHeight: 1.2,
          margin: 0, color: T.text,
        }}>{t('tax_sc_decl_headline')}</h3>
        <p style={{
          fontSize: 13.5, color: T.sub, lineHeight: 1.6, margin: 0,
        }}>
          {t('tax_sc_decl_body')}
        </p>
      </div>
    </div>
  );
}

// Früher getrackte Versalien — jetzt ruhiges sentence-case Label (Calm).
function CapsLabel({ T, children }) {
  return (
    <span style={{
      fontFamily: F.family, fontSize: 12, fontWeight: 600,
      letterSpacing: '-0.005em',
      color: T.sub,
    }}>{children}</span>
  );
}

// Desktop-Jahres-Auswahl nutzt das geteilte <Segmented> (gleitender Thumb).
// Mobile: kompakter Trigger oben rechts → öffnet das SelectSheet (Bottom-Sheet
// von unten) statt eines Dropdowns.
function YearTrigger({ year, onClick, T }) {
  return (
    <button
      className="mo-row"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 2px',
        background: 'transparent',
        border: 'none', borderRadius: 0,
        color: T.text, cursor: 'pointer',
        fontFamily: F.family, fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em',
        WebkitTapHighlightColor: 'transparent',
        flexShrink: 0,
      }}
    >
      {year}
      <span className="mo-drop" style={{ display: 'inline-flex' }}>
        <Icon d={Icons.chevD} size={13} stroke={T.sub} sw={2.2} />
      </span>
    </button>
  );
}

// ── HALTEFRIST-AUFSCHLÜSSELUNG ───────────────────────────────────────────────
// Ersetzt die früher irreführende „Behalten vs. Abzug"-Quote. Zeigt die ECHTE
// Logik: realisiertes Ergebnis aufgeteilt nach Haltedauer (über/unter 1 Jahr),
// darunter rechnerischer Abzug + was real dieses Jahr bleibt. Geteilt von
// Desktop + Mobile, damit beide Pfade nie auseinanderlaufen.
// Symbolische Waage (knüpft ans Bilanz-Icon an): zwei Töpfe „über/unter 1 Jahr".
// Die betragsgrößere („schwerere") Seite dippt. Rein erklärend-dekorativ.
function BalanceScale({ color, freeHeavier }) {
  const lY = freeHeavier ? 52 : 34;
  const rY = freeHeavier ? 34 : 52;
  return (
    <svg viewBox="0 0 200 120" width="100%" style={{ maxWidth: 260, display: 'block', margin: '10px auto 2px' }}
      fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M80 104 H120" />
      <path d="M88 104 L100 92 L112 104" />
      <path d="M100 92 V42" />
      <path d={`M42 ${lY} L158 ${rY}`} />
      <path d={`M42 ${lY} V${lY + 14}`} /><path d={`M158 ${rY} V${rY + 14}`} />
      <path d={`M28 ${lY + 14} a13 8 0 0 0 28 0`} />
      <path d={`M144 ${rY + 14} a13 8 0 0 0 28 0`} />
      <circle cx="100" cy="41" r="3.4" fill="#E4544A" stroke="none" />
    </svg>
  );
}

// Ein Verkauf in der Haltedauer-Aufschlüsselung: Coin · Kauf→Verkauf · Dauer · Ergebnis.
function SaleRow({ T, sale, durText, convert, symbol, locale, t }) {
  const gv = Number(sale.gv) || 0;
  const pos = gv >= 0;
  const dates = sale.kaufDatum
    ? `${fmtDateDE(sale.kaufDatum)} → ${fmtDateDE(sale.datum)}`
    : (sale.datum ? fmtDateDE(sale.datum) : t('tax_basis_incomplete'));
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      padding: '9px 0', borderTop: `0.5px solid ${withAlpha(T.text, 0.06)}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sale.name || sale.sym}
        </div>
        <div style={{ fontSize: 11, color: T.sub, marginTop: 1 }}>
          {dates}{durText ? ` · ${durText}` : ''}
        </div>
      </div>
      <span style={{
        ...MONO_FEAT, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
        color: Math.abs(gv) < 0.005 ? T.sub : (pos ? (T.gain || T.green) : (T.loss || T.red)),
      }}>
        {pos ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(gv)), locale)}</Private> {symbol}
      </span>
    </div>
  );
}

// Klartext-Antwort (immer sichtbar): EINE große Aussage + Unterzeile + winzige
// Fußnote (Disclaimer bleibt sichtbar). Coinbase/iOS-Health-Gefühl: sofort klar,
// fast ohne Lesen. Der Detail-Block (Flow/Haltefrist) liegt hinter „Details".
function ResultSummary({ T, report, rules, isWide }) {
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const { realizedPnL, taxObligation } = report;
  const gain = T.gain || T.green;
  const loss = T.loss || T.red;
  const pct = ((rules?.rate || 0) * 100).toLocaleString(locale, { maximumFractionDigits: 1 });
  const eur = (v) => `${fmtEur(convert(v), locale)} ${symbol}`;
  const mask = useMaskText();

  const isAT = rules?.code === 'AT';
  const isLoss = realizedPnL < -0.005;
  const hasDed = taxObligation > 0.005;
  const keptEur = realizedPnL - taxObligation;
  const title = isLoss ? t('tax_answer_loss_title')
    : hasDed ? t('tax_answer_keep_some_title', { ded: mask(eur(taxObligation)) })
    : t('tax_answer_keep_all_title');
  const sub = isLoss ? (isAT ? t('tax_answer_loss_sub_at') : t('tax_answer_loss_sub'))
    : hasDed ? t('tax_answer_keep_some_sub', { kept: mask(eur(Math.max(0, keptEur))) })
    : t('tax_answer_keep_all_sub');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!isLoss && (
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={gain}
            strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path d="M8.5 12.4l2.4 2.4 4.6-5" />
          </svg>
        )}
        <span style={{
          fontFamily: F.display, fontSize: isWide ? 22 : 19, fontWeight: 700,
          letterSpacing: '-0.02em', color: isLoss ? loss : T.text,
        }}>{title}</span>
      </div>
      <div style={{
        fontSize: 14, color: T.sub, marginTop: 7, lineHeight: 1.5,
        paddingLeft: isLoss ? 0 : 32,
      }}>{sub}</div>
      <div style={{
        fontSize: 11, color: T.mute, marginTop: 10, lineHeight: 1.5,
        paddingLeft: isLoss ? 0 : 32,
      }}>{t('tax_inline_disclaimer_short', { pct })}</div>
    </div>
  );
}

// Wrapper: Antwort immer sichtbar, Detail-Aufschlüsselung hinter „Details anzeigen".
// „Bilanz"-Bottom-Sheet: fährt von unten hoch und zeigt die genaue individuelle
// Rechnung (Gewinn-Flow + Haltedauer-Split + Abzug + Schlusszeile). Gleiches
// Motion-/Drag-Pattern wie SelectSheet.
function BilanzSheet({ open, onClose, T, report, rules, isWide, year }) {
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const { realizedPnL, taxableGain, freeGain, taxObligation } = report;
  const gain = T.gain || T.green;
  const loss = T.loss || T.red;
  const [openBucket, setOpenBucket] = useState(null); // 'free' | 'tax' | null

  // AT (Österreich): KEINE 1-Jahres-Frist für Neuvermögen — die Töpfe sind
  // Altvermögen (vor 1.3.2021, frei) vs. Neuvermögen (zählt). Außerdem kein
  // Verlustvortrag → andere Verlust-Schlusszeile.
  const isAT = rules?.code === 'AT';
  const eur = (v) => `${fmtEur(convert(v), locale)} ${symbol}`;
  const mask = useMaskText();
  const pct = ((rules?.rate || 0) * 100).toLocaleString(locale, { maximumFractionDigits: 1 });
  const keptEur = realizedPnL - taxObligation;
  const conclusion = realizedPnL < -0.005
    ? (isAT ? t('tax_carryloss_note_at') : t('tax_carryloss_note'))
    : taxObligation > 0.005
      ? t('tax_keep_some', { ded: mask(eur(taxObligation)), kept: mask(eur(Math.max(0, keptEur))) })
      : t('tax_keep_all');

  // Alle Verkäufe nach Haltedauer-Topf (über/unter 1 Jahr = !stpfl / stpfl).
  const allSales = [];
  (report.perAsset || []).forEach((a) => (a.events || []).forEach((ev) => allSales.push({ ...ev, sym: a.sym, name: a.name })));
  const byTs = (x, y) => ((y.effectiveTimestamp || y.datum || '') < (x.effectiveTimestamp || x.datum || '') ? -1 : 1);
  const freeSales = allSales.filter((e) => !e.stpfl).sort(byTs);
  const taxSales = allSales.filter((e) => e.stpfl).sort(byTs);
  const freeHeavier = Math.abs(freeGain) >= Math.abs(taxableGain);
  const activeSales = openBucket === 'free' ? freeSales : openBucket === 'tax' ? taxSales : null;

  // AT-Abrechnung: flacher Satz auf Neuvermögen, Altvermögen frei, kein Vortrag.
  const rate = rules?.rate || 0;
  const keepPctN = (1 - rate) * 100;
  const keepPctStr = keepPctN.toLocaleString(locale, { maximumFractionDigits: 1 });
  const hasAlt = Math.abs(freeGain) >= 0.005;
  const showAtBar = taxObligation > 0.005;
  const atRemains = realizedPnL - taxObligation;
  const atSales = [...allSales].sort(byTs);

  // Zeile in der AT-Abrechnung (Label [+ Sub] · Betrag rechts).
  const calcRow = (label, sub, value, valueColor, opts = {}) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
      borderTop: opts.top ? `${opts.thick ? 1.5 : 0.5}px solid ${withAlpha(T.text, opts.thick ? 0.18 : 0.1)}` : 'none',
      marginTop: opts.top ? 14 : 12, paddingTop: opts.top ? 13 : 0,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: opts.strong ? F.display : F.family, fontSize: opts.strong ? 15 : 13.5, fontWeight: opts.strong ? 700 : 600, color: T.text }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>{sub}</div>}
      </div>
      <span style={{ ...MONO_FEAT, fontFamily: F.display, fontSize: opts.strong ? 19 : 16, fontWeight: 700, color: valueColor || T.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{value}</span>
    </div>
  );

  const fmtHold = (days) => {
    if (days == null || !isFinite(days)) return null;
    if (days < 31) return `${Math.round(days)} ${t('days_short')}`;
    const y = Math.floor(days / 365);
    const m = Math.floor((days % 365) / 30.4);
    if (y >= 1) return m > 0 ? `${y} ${t('tax_dur_y')} ${m} ${t('tax_dur_m')}` : `${y} ${t('tax_dur_y')}`;
    return `${m} ${t('tax_dur_m')}`;
  };

  const Pan = ({ id, kicker, value, sales }) => {
    const isOpen = openBucket === id;
    const n = sales.length;
    const numColor = Math.abs(value) < 0.005 ? T.sub : (value >= 0 ? gain : loss);
    return (
      <button onClick={() => setOpenBucket(isOpen ? null : id)} style={{
        flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none',
        borderTop: `0.5px solid ${T.line}`, padding: '14px 0 4px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
      }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em', color: id === 'free' ? gain : loss }}>{kicker}</div>
        <div style={{ ...MONO_FEAT, fontFamily: F.display, fontSize: isWide ? 24 : 22, fontWeight: 700, letterSpacing: '-0.03em', color: numColor, marginTop: 6 }}>
          {value >= 0 ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(value)), locale)}</Private> {symbol}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: T.sub, marginTop: 6 }}>
          {n} {n === 1 ? t('tax_bal_sale') : t('tax_bal_sales')}
          <span style={{ display: 'inline-flex', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
            <Icon d={Icons.chevD} size={12} stroke={T.sub} sw={2.2} />
          </span>
        </div>
      </button>
    );
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div key="bilanz-bd"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 400, touchAction: 'none' }} />
      )}
      {open && (
        <motion.div key="bilanz-sheet"
          initial={{ y: '112%' }} animate={{ y: 0 }} exit={{ y: '112%' }}
          transition={{ type: 'spring', stiffness: 420, damping: 40, mass: 0.9 }}
          drag="y" dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0, bottom: 0.5 }}
          onDragEnd={(_e, info) => { if (info.offset.y > 90 || info.velocity.y > 600) onClose(); }}
          style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, margin: '0 auto', width: '100%', maxWidth: 540,
            background: T.surface, borderTop: `1px solid ${T.line}`, borderRadius: '24px 24px 0 0',
            padding: `14px 20px calc(env(safe-area-inset-bottom, 0px) + 22px)`, zIndex: 401, color: T.text,
            boxShadow: '0 -14px 44px rgba(0,0,0,0.45)',
          }}>
          <div style={{ width: 40, height: 4, borderRadius: 999, background: T.line, margin: '0 auto 16px', cursor: 'grab' }} />
          <div style={{ padding: '0 2px 4px', textAlign: 'center' }}>
            <div style={{ fontFamily: F.display, fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
              {t('tax_balance_title', { year })}
            </div>
          </div>
          <div style={{ maxHeight: '64vh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', marginTop: 4 }}>
            {isAT ? (
              /* ── Österreich: flache Abrechnung (27,5 %, keine Haltefrist) ── */
              <>
                {hasAlt && calcRow(t('tax_at_alt_label'), t('tax_at_alt_sub'),
                  <>{freeGain >= 0 ? '+' : '−'}<Private>{`${fmtEur(convert(Math.abs(freeGain)), locale)} ${symbol}`}</Private></>,
                  freeGain >= 0 ? gain : loss)}
                {Math.abs(taxableGain) >= 0.005 && calcRow(t('tax_at_gain_label'), null,
                  <>{taxableGain >= 0 ? '+' : '−'}<Private>{`${fmtEur(convert(Math.abs(taxableGain)), locale)} ${symbol}`}</Private></>,
                  taxableGain >= 0 ? gain : loss, { top: hasAlt })}

                {showAtBar && (
                  <>
                    <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', gap: 2, marginTop: 18 }}>
                      <div style={{ width: `${keepPctN}%`, background: gain }} />
                      <div style={{ width: `${rate * 100}%`, background: loss }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 9, fontSize: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: gain }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: gain }} />{t('tax_at_keep_pct', { pct: keepPctStr })}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: loss }}>
                        {t('tax_at_setaside_pct', { pct })}<span style={{ width: 8, height: 8, borderRadius: '50%', background: loss }} />
                      </span>
                    </div>
                    {calcRow(t('tax_at_deduction', { pct }), null, <>−<Private>{`${fmtEur(convert(taxObligation), locale)} ${symbol}`}</Private></>, loss, { top: true })}
                  </>
                )}

                <button onClick={() => setOpenBucket(openBucket === 'all' ? null : 'all')} style={{
                  marginTop: 18, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: 'transparent', border: 'none', borderTop: `0.5px solid ${T.line}`, padding: '14px 0 2px',
                  fontFamily: F.family, fontSize: 13, fontWeight: 700, color: T.sub, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}>
                  {atSales.length} {atSales.length === 1 ? t('tax_bal_sale') : t('tax_bal_sales')}
                  <span style={{ display: 'inline-flex', transform: openBucket === 'all' ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                    <Icon d={Icons.chevD} size={13} stroke={T.sub} sw={2.2} />
                  </span>
                </button>
                {openBucket === 'all' && (
                  <div style={{ marginTop: 4 }}>
                    {atSales.map((s, i) => (
                      <SaleRow key={i} T={T} sale={s} durText={fmtHold(s.hf)} convert={convert} symbol={symbol} locale={locale} t={t} />
                    ))}
                  </div>
                )}

                <div style={{ position: 'sticky', bottom: 0, background: T.surface, marginTop: 16, paddingBottom: 2 }}>
                  {showAtBar
                    ? calcRow(t('tax_at_remains'), null, <Private>{`${fmtEur(convert(Math.max(0, atRemains)), locale)} ${symbol}`}</Private>, gain, { top: true, thick: true, strong: true })
                    : (
                      <div style={{ paddingTop: 14, borderTop: `1.5px solid ${withAlpha(T.text, 0.18)}` }}>
                        <div style={{ fontFamily: F.family, fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.5 }}>{conclusion}</div>
                      </div>
                    )}
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 11.5, color: T.sub, lineHeight: 1.5 }}>{t('tax_at_disclaimer')}</div>
                    <div style={{ fontSize: 11, color: T.mute, marginTop: 6, lineHeight: 1.5 }}>{t('tax_inline_disclaimer_short', { pct })}</div>
                  </div>
                </div>
              </>
            ) : (
              /* ── Deutschland: Waage (über/unter 1 Jahr) ── */
              <>
                <BalanceScale color={withAlpha(T.text, 0.78)} freeHeavier={freeHeavier} />
                <div style={{ display: 'flex', gap: 16 }}>
                  <Pan id="free" kicker={t('tax_bal_over')} value={freeGain} sales={freeSales} />
                  <Pan id="tax" kicker={t('tax_bal_under')} value={taxableGain} sales={taxSales} />
                </div>
                {activeSales && (
                  <div style={{ marginTop: 8 }}>
                    {activeSales.length === 0
                      ? <div style={{ fontSize: 12, color: T.sub, padding: '10px 0' }}>—</div>
                      : activeSales.map((s, i) => (
                          <SaleRow key={i} T={T} sale={s} durText={fmtHold(s.hf)} convert={convert} symbol={symbol} locale={locale} t={t} />
                        ))}
                  </div>
                )}
                <div style={{ position: 'sticky', bottom: 0, background: T.surface, marginTop: 16, paddingBottom: 2 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    borderTop: `1.5px solid ${withAlpha(T.text, 0.18)}`, paddingTop: 14,
                  }}>
                    <span style={{ fontFamily: F.display, fontSize: 15, fontWeight: 700, color: T.text }}>{t('tax_bal_result')}</span>
                    <span style={{ ...MONO_FEAT, fontFamily: F.display, fontSize: 17, fontWeight: 700, color: realizedPnL >= 0 ? gain : loss }}>
                      {realizedPnL >= 0 ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(realizedPnL)), locale)}</Private> {symbol}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function ResultBreakdown({ T, report, rules, isWide, year }) {
  const { t } = useT();
  const [sheet, setSheet] = useState(false);
  return (
    <div>
      <div style={{
        borderTop: `1px solid ${T.line}`, paddingTop: isWide ? 22 : 18, marginTop: isWide ? 4 : 2,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ResultSummary T={T} report={report} rules={rules} isWide={isWide} />
        </div>
        {/* Runder Koralle-Button rechts → öffnet die Bilanz. Weiße Waage (eigenes Icon). */}
        <button
          aria-label={t('tax_balance_btn')}
          title={t('tax_balance_btn')}
          onClick={() => setSheet(true)}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.94)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          style={{
            flexShrink: 0, width: 46, height: 46, borderRadius: 999,
            background: '#E4544A', border: 'none',
            boxShadow: '0 3px 10px rgba(0,0,0,0.16)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent', transition: 'transform .12s',
          }}
        >
          <svg width={23} height={23} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF"
            strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 4v16" /><path d="M7.5 20h9" /><path d="M5 7.5h14" />
            <path d="M5 7.5 2.6 12.4a2.4 2.4 0 0 0 4.8 0Z" /><path d="M19 7.5l-2.4 4.9a2.4 2.4 0 0 0 4.8 0Z" />
            <circle cx="12" cy="5" r="1.1" fill="#FFFFFF" stroke="none" />
          </svg>
        </button>
      </div>
      <BilanzSheet open={sheet} onClose={() => setSheet(false)} T={T} report={report} rules={rules} isWide={isWide} year={year} />
    </div>
  );
}

// ── REPORTS DESKTOP SCREEN ───────────────────────────────────────────────────
// Editorial-Redesign der Mobile-Berichts-Ansicht: Eyebrow + Year-Pills,
// Display-Headline mit realisiertem PnL als Hero-Number, Haltefrist-Aufschlüsselung,
// Coin-Tabelle, Disclaimer-Dokument.
function ReportsDesktopScreen({ report, year, years, onYearChange, isDE, rules, txs, marketData: marketDataProp, onExport, onDrilldown, exporting, exportError }) {
  const { T, isDark } = useTheme();
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const { realizedPnL, costBasis,
          eventsCount, perAsset, warnings } = report;

  const isTax = rules?.reportMode === 'tax';
  const isAverage = rules?.costBasis === 'average';
  const methodWord = isAverage ? t('tax_method_long') : 'FIFO';
  const gain = T.gain || T.green;

  // Live-Markt-Daten + aktueller Restbestand pro Coin.
  // Context bevorzugt, Prop als Fallback.
  const { marketData: marketDataCtx } = useMarketData();
  const marketData = marketDataCtx && Object.keys(marketDataCtx).length ? marketDataCtx : (marketDataProp || {});

  // currentHoldings: { [sym]: { menge, invested, price, value, coinId } }
  const currentHoldings = useMemo(() => {
    const portfolio = calcPortfolio(txs); // { [sym]: { menge, invested, ... } }
    const out = {};
    for (const sym of Object.keys(portfolio)) {
      const p = portfolio[sym];
      if (!p || !(p.menge > 0)) continue;
      const coinId = COIN_MAP[sym]?.id || null;
      const price = coinId ? (marketData?.[coinId]?.price || 0) : 0;
      out[sym] = {
        menge: p.menge,
        invested: p.invested || 0,
        price,
        value: p.menge * price,
        coinId,
      };
    }
    return out;
  }, [txs, marketData]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: T.text, fontFamily: F.family }}>

      {/* Eyebrow: "JAHRESBERICHT" + line + Year-Pills + Export-Button */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <span style={{
            fontFamily: F.family, fontSize: 11, fontWeight: 700,
            letterSpacing: '-0.005em', color: T.text,
          }}>{t('tax_annual_report')}</span>
          <div style={{ flex: 1, height: 1, background: T.line }} />
          {years.length > 1 && (
            <Segmented
              bare
              value={year}
              onChange={onYearChange}
              options={years.map(y => ({ value: y, label: String(y) }))}
            />
          )}
          {onExport && (
            <button
              onClick={onExport}
              disabled={exporting}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 16px',
                background: T.surface,
                border: `1px solid ${T.line}`,
                borderRadius: 999,
                color: T.text,
                fontFamily: F.family, fontWeight: 600, fontSize: 13,
                letterSpacing: '-0.01em',
                cursor: exporting ? 'default' : 'pointer',
                opacity: exporting ? 0.6 : 1,
                boxShadow: isDark ? '0 8px 22px rgba(0,0,0,0.30)' : '0 8px 22px rgba(34,30,24,0.12)',
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform .12s',
              }}
              onMouseDown={(e) => { if (!exporting) e.currentTarget.style.transform = 'scale(0.96)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <Icon d={Icons.export} size={14} stroke={T.text} sw={2.2} style={{ color: T.text }} />
              {exporting ? t('tax_generating') : t('tax_export')}
            </button>
          )}
        </div>
        {exportError && (
          <div style={{ fontSize: 11, color: T.red, textAlign: 'right', marginTop: -8 }}>
            {exportError}
          </div>
        )}
        <div style={{ borderBottom: `1px solid ${T.line}`, paddingBottom: 16 }}>
          <h2 style={{
            fontFamily: F.display, fontSize: 49, fontWeight: 800,
            letterSpacing: '-0.046em', lineHeight: 0.98, margin: 0, color: T.text,
          }}>
            {realizedPnL >= 0 ? t('tax_realized_gain') : t('tax_realized_loss')} {year}.
          </h2>
          <p style={{
            margin: '14px 0 0', fontSize: 15, color: T.sub,
            lineHeight: 1.5, maxWidth: 680,
          }}>
            {(isAverage ? t('tax_avg_over_sales') : t('tax_fifo_over_sales'))} {eventsCount}{' '}
            {eventsCount === 1 ? t('tax_event_in_year') : t('tax_events_in_year')}
          </p>
        </div>
      </div>

      {/* Realisiertes Ergebnis — Hero-Number (flach, kein Box-Container) */}
      <div>
        <CapsLabel T={T}>
          {isAverage
            ? (realizedPnL >= 0 ? t('tax_realized_gain_average') : t('tax_realized_loss_average'))
            : (realizedPnL >= 0 ? t('tax_realized_gain_fifo') : t('tax_realized_loss_fifo'))}
        </CapsLabel>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 12 }}>
          <span style={{
            fontFamily: F.display, fontSize: 52, fontWeight: 700,
            letterSpacing: '-0.055em', lineHeight: 0.95,
            color: realizedPnL >= 0 ? gain : (T.loss || T.red),
          }}>
            {realizedPnL >= 0 ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(realizedPnL)), locale)}</Private>
          </span>
          <span style={{ fontFamily: F.display, fontSize: 22, fontWeight: 600, color: T.sub }}>{symbol}</span>
        </div>
        {costBasis > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.sub, lineHeight: 1.5 }}>
            {t('tax_cost_of_sold')} <span style={{ ...MONO_FEAT, color: T.text, fontWeight: 600 }}><Private>{fmtEur(convert(costBasis), locale)}</Private> {symbol}</span>
          </div>
        )}
      </div>

      {/* Ergebnis-Antwort (immer sichtbar) + genaue Rechnung im „Bilanz"-Sheet */}
      {isTax && eventsCount > 0 && (
        <ResultBreakdown T={T} report={report} rules={rules} isWide year={year} />
      )}

      {/* Hinweise — flach, goldenes Icon + Hairline (kein Box-Container). */}
      {warnings.length > 0 && (
        <div style={{
          borderTop: `1px solid ${T.line}`, paddingTop: 16,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
            stroke={T.gold || '#C9A227'} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: 1 }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>{t('tax_calc_notes')}</div>
            {warnings.map((w, i) => (
              <div key={i} style={{
                fontSize: 13, color: T.sub, lineHeight: 1.55,
                marginBottom: i === warnings.length - 1 ? 0 : 4,
              }}>{w.message}</div>
            ))}
          </div>
        </div>
      )}

      {/* Coin-Tabelle — flach (kein Box-Container, kein getönter Header) */}
      {perAsset.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 4 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr 40px',
            gap: 16, padding: '14px 8px',
            borderBottom: `1px solid ${T.line}`,
          }}>
            <CapsLabel T={T}>{t('coin')}</CapsLabel>
            <CapsLabel T={T}>{t('tax_turnover')}</CapsLabel>
            <CapsLabel T={T}>{t('tax_events')}</CapsLabel>
            <span style={{
              fontFamily: F.family, fontSize: 11.5, fontWeight: 700,
              letterSpacing: '-0.005em', 
              color: T.mute, textAlign: 'right',
            }}>{t('tax_realized')}</span>
            <span />
          </div>
          {perAsset.map((a, i) => {
            const pnlColor = Math.abs(a.realizedPnL) < 0.5 ? T.sub
                            : (a.realizedPnL >= 0 ? gain : (T.loss || T.red));
            const turnover = a.sellGross > 0 ? a.sellGross : (a.swapGross || a.grossProceeds || 0);
            const turnoverLabel = a.sellGross > 0 ? t('tax_sold') : t('tax_swapped');
            const hold = currentHoldings[a.sym];
            const holdValue = hold?.value || 0;
            const holdAmt = hold?.menge || 0;
            return (
              <button
                key={a.sym}
                className="mo-row"
                onClick={() => onDrilldown?.(a.sym)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr 40px',
                  gap: 16, alignItems: 'center',
                  padding: '14px 8px',
                  width: '100%', textAlign: 'left',
                  background: 'transparent', border: 'none',
                  borderTop: i === 0 ? 'none' : `1px solid ${T.line}`,
                  color: T.text, cursor: 'pointer', fontFamily: F.family,
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.itemHover || T.surfaceHi; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <CoinImage sym={a.sym} marketData={marketData} size={32} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontFamily: F.display, fontSize: 15, fontWeight: 700,
                      letterSpacing: '-0.02em', color: T.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{a.name}</div>
                    <div style={{ fontSize: 11.5, color: T.sub, marginTop: 1 }}>{a.sym}</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: T.text }}>
                  <div>
                    <span style={{ ...MONO_FEAT, fontWeight: 600 }}><Private>{fmtEur(convert(turnover), locale)}</Private> {symbol}</span>
                    <span style={{ color: T.sub, fontSize: 11, marginLeft: 6 }}>{turnoverLabel}</span>
                  </div>
                  {holdAmt > 0 && holdValue > 0 && (
                    <div style={{ ...MONO_FEAT, fontSize: 11, color: T.sub, marginTop: 2 }}>
                      {t('tax_today')} · <Private>{fmtEur(convert(holdValue), locale)}</Private> {symbol} <span style={{ color: T.mute }}>(<Private>{coinAmtDE(holdAmt)}</Private> {a.sym})</span>
                    </div>
                  )}
                </div>
                <div style={{ ...MONO_FEAT, fontSize: 13, color: T.text, fontWeight: 600 }}>
                  {a.eventsCount}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    ...MONO_FEAT, fontSize: 15, fontWeight: 700, color: pnlColor,
                  }}>
                    {a.realizedPnL >= 0 ? '+' : '−'}<Private>{fmt(convert(Math.abs(a.realizedPnL)), locale)}</Private>
                    <span style={{ fontSize: 11, opacity: 0.6 }}> {symbol}</span>
                  </div>
                  {a.hasCostBasisUnknown && (
                    <div style={{ fontSize: 10, color: T.gold || '#C9A227', marginTop: 2 }}>
                      {t('tax_basis_incomplete')}
                    </div>
                  )}
                </div>
                <svg className="mo-chev" width={14} height={14} viewBox="0 0 24 24" fill="none"
                  stroke={T.mute} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                  style={{ justifySelf: 'end' }}>
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            );
          })}
          {/* Gesamt — die Spalte summiert sich sichtbar zur großen Zahl oben */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1.8fr 1.2fr 1fr 1fr 40px',
            gap: 16, alignItems: 'center', padding: '16px 8px',
            borderTop: `2px solid ${T.line}`,
          }}>
            <div style={{ fontFamily: F.display, fontSize: 14, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>{t('tax_total')}</div>
            <span /><span />
            <div style={{ textAlign: 'right', ...MONO_FEAT, fontSize: 15, fontWeight: 700, color: realizedPnL >= 0 ? gain : (T.loss || T.red) }}>
              {realizedPnL >= 0 ? '+' : '−'}<Private>{fmt(convert(Math.abs(realizedPnL)), locale)}</Private><span style={{ fontSize: 11, opacity: 0.6 }}> {symbol}</span>
            </div>
            <span />
          </div>
        </div>
      )}

      {/* Footer-Hinweis — flach, kein Dokument-Chrome (Erklärung №/Seite raus) */}
      <div style={{
        borderTop: `1px solid ${T.line}`, paddingTop: 22,
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 24, alignItems: 'start',
      }}>
        <h3 style={{
          fontFamily: F.display, fontSize: 22, fontWeight: 700,
          letterSpacing: '-0.03em', lineHeight: 1.2,
          margin: 0, color: T.text,
        }}>{t('tax_rp_decl_headline')}</h3>
        <p style={{
          fontSize: 13.5, color: T.sub, lineHeight: 1.6, margin: 0,
        }}>
          {t('tax_rp_decl_body', { year, method: methodWord })}
        </p>
      </div>
    </div>
  );
}

// ── MAIN SCREEN ───────────────────────────────────────────────────────────────

export default function HistorieScreen({ view: viewProp = 'smartcheck', txs, marketData, onOpenPricing, onSelectAsset, onOpenAlert, onDrilldown }) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint();
  const { isPremium } = useUserPlan();
  const { country } = useProfile();
  const isDE = country === 'DE';
  const rules = rulesFor(country);
  const isTax = rules.reportMode === 'tax';
  // Smart Check (1-Jahres-Haltefrist) ist DE-spezifisch. Länder ohne Frist-Konzept
  // (AT: Neuvermögen ohne Frist; übrige: keine Bewertung) bekommen den Jahres-Report.
  const view = (viewProp === 'smartcheck' && !rules.showSmartCheck) ? 'reports' : viewProp;
  const { t } = useT();
  const { convert, symbol, locale } = useCurrency();
  const [showExport, setShowExport] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [yearSheet, setYearSheet] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [drilldownSym, setDrilldownSym] = useState(null);

  // Single Source of Truth: ein Aufruf, alle Zahlen daraus.
  const report = useMemo(
    () => buildReport({ txs, year, isDE, country, marketData }),
    [txs, year, isDE, country, marketData]
  );

  const years = useMemo(() => listReportYears(txs).slice(0, 4), [txs]);

  const handleExportPick = async (format, pickedYear) => {
    setShowExport(false);
    setExportError('');
    const y = pickedYear || year;
    if (format !== 'pdf' && format !== 'xlsx') return;
    setExporting(true);
    try {
      await exportYearReport({ format, txs, year: y, isDE, country, marketData });
    } catch (err) {
      setExportError(err?.message || t('tax_export_failed'));
    } finally {
      setExporting(false);
    }
  };

  // ── Empty State ──────────────────────────────────────────────────────────
  if (!txs || txs.length === 0) {
    return (
      <div style={{ padding: '40px 20px 100px', textAlign: 'center' }}>
        <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em', marginBottom: 8, color: T.text }}>
          {t('tax_empty_title')}
        </div>
        <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
          {t('tax_empty_line1')}<br/>{t('tax_empty_line2')}
        </div>
      </div>
    );
  }

  // ── Drilldown ────────────────────────────────────────────────────────────
  if (drilldownSym) {
    const asset = report.perAsset.find(a => a.sym === drilldownSym);
    if (asset) {
      // Restbestand für "Heute · Live"-Spalte — calcPortfolio liefert
      // { [sym]: { menge, invested, ... } } über alle Jahre.
      const portfolio = calcPortfolio(txs);
      const heldAmount = portfolio[asset.sym]?.menge || 0;
      return (
        <DrilldownView
          T={T}
          asset={{ ...asset, heldAmount }}
          year={year}
          isDE={isDE}
          marketData={marketData}
          onBack={() => setDrilldownSym(null)}
        />
      );
    }
  }

  // ── DESKTOP: Smart Check (FIFO-Timeline) — Handoff red-screens-tax.jsx ──
  if (isWide && view === 'smartcheck') {
    return (
      <div style={{ color: T.text }}>
        <SmartCheckScreen txs={txs} marketData={marketData} onSelectAsset={onSelectAsset} onOpenAlert={onOpenAlert} />
      </div>
    );
  }

  // ── DESKTOP: Berichte (Jahres-Report — Editorial Redesign der Mobile-View) ──
  if (isWide && view === 'reports') {
    return (
      <div style={{ color: T.text }}>
        {showExport && (
          <ExportSheet
            open={showExport}
            onClose={() => setShowExport(false)}
            scope="year"
            availableYears={years}
            isPremium={isPremium}
            onPick={handleExportPick}
            onLocked={() => { setShowExport(false); onOpenPricing?.(); }}
          />
        )}
        <ReportsDesktopScreen
          report={report}
          year={year}
          years={years}
          onYearChange={setYear}
          isDE={isDE}
          rules={rules}
          txs={txs}
          marketData={marketData}
          onExport={() => setShowExport(true)}
          onDrilldown={setDrilldownSym}
          exporting={exporting}
          exportError={exportError}
        />
      </div>
    );
  }

  const { realizedPnL, costBasis,
          eventsCount, perAsset, warnings } = report;

  return (
    <div style={{ padding: isWide ? '8px 0 40px' : '8px 0 100px', color: T.text, maxWidth: isWide ? 980 : 'none', margin: isWide ? '0 auto' : undefined }}>

      {/* Title + Year-Dropdown — kompaktes Dropdown oben rechts (spart Platz,
          läuft nicht in den „Berichte"-Schriftzug). */}
      <div style={{ padding: '8px 18px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: F.display, fontSize: 30, fontWeight: 700, letterSpacing: '-0.04em', margin: 0 }}>
          {t('reports') || 'Berichte'}
        </h1>
        {years.length > 1 && (
          <YearTrigger year={year} onClick={() => setYearSheet(true)} T={T} />
        )}
      </div>

      {/* Jahr-Auswahl als Bottom-Sheet (von unten, Spring-Motion) */}
      <SelectSheet
        open={yearSheet}
        title={t('tax_year') || 'Jahr'}
        value={year}
        onSelect={setYear}
        onClose={() => setYearSheet(false)}
        options={years.map(y => ({ value: y, label: String(y) }))}
      />

      {/* HERO: Realisiertes Ergebnis — Editorial Cover-Hero (2px top border in text) */}
      <div style={{ padding: isWide ? '0 0 12px' : '0 18px 12px' }}>
        <div style={{
          position: 'relative',
          borderRadius: mr(16),
          padding: isWide ? '24px 26px 26px' : '20px 22px 22px',
          background: T.surface,
          border: `1px solid ${T.line}`,
          boxShadow: 'none',
          overflow: 'hidden',
        }}>
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '-0.005em',
            color: T.sub, marginBottom: 10,
          }}>
            {realizedPnL >= 0 ? t('tax_realized_gain') : t('tax_realized_loss')} · {rules.costBasis === 'average' ? t('tax_method_average') : 'FIFO'}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{
              fontFamily: F.display, fontSize: 56, fontWeight: 700,
              letterSpacing: '-0.055em', lineHeight: 0.95,
              color: realizedPnL >= 0 ? T.green : (T.loss || T.red),
            }}>
              {realizedPnL >= 0 ? '+' : '−'}<Private>{fmtEur(convert(Math.abs(realizedPnL)), locale)}</Private>
            </span>
            <span style={{ fontFamily: F.display, fontSize: 22, fontWeight: 600, color: T.sub }}>{symbol}</span>
          </div>

          {/* Anschaffung-Kontext, dezent */}
          {costBasis > 0 && (
            <div style={{ marginTop: 12, fontSize: 11.5, color: T.sub, lineHeight: 1.55 }}>
              {t('tax_cost_of_sold')} <Private>{fmtEur(convert(costBasis), locale)}</Private> {symbol}
            </div>
          )}
        </div>
      </div>

      {/* Ergebnis-Antwort (immer sichtbar) + genaue Rechnung im „Bilanz"-Sheet */}
      {isTax && eventsCount > 0 && (
        <div style={{ padding: '0 18px 18px' }}>
          <ResultBreakdown T={T} report={report} rules={rules} isWide={false} year={year} />
        </div>
      )}

      {/* Hinweise — flach (kein getöntes Kästchen): goldenes Icon + Hairline. */}
      {warnings.length > 0 && (
        <div style={{ padding: '4px 18px 18px' }}>
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', paddingTop: 14, borderTop: `1px solid ${T.lineSoft || T.line}` }}>
            <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
              stroke={T.gold} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.55 }}>
              <span style={{ fontWeight: 700 }}>{t('tax_calc_notes')}. </span>
              {warnings.map((w, i) => (
                <span key={i}>{w.message}{i < warnings.length - 1 ? ' ' : ''}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Per-Coin-Liste — antippbar fuer Drilldown */}
      {perAsset.length > 0 && (
        <>
          <SectionHeader title={`${t('tax_your_coins')} · ${year}`} />
          <div style={{ margin: '0 18px', borderRadius: 18, background: T.surface, border: `1px solid ${T.line}`, overflow: 'hidden' }}>
            {perAsset.map((a, i) => (
              <button
                key={a.sym}
                className="mo-row"
                onClick={() => onDrilldown ? onDrilldown(a.sym, year) : setDrilldownSym(a.sym)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '14px 16px',
                  borderBottom: i === perAsset.length - 1 ? 'none' : `1px solid ${T.line}`,
                  background: 'none', border: 'none', borderRadius: 0,
                  cursor: 'pointer',
                  color: T.text, fontFamily: F.family,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <CoinImage sym={a.sym} marketData={marketData} size={34} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: F.display, fontSize: 14, fontWeight: 600, letterSpacing: '-0.02em' }}>
                      {a.name}
                    </div>
                    <div style={{ fontFamily: F.mono, fontSize: 10.5, color: T.sub, marginTop: 2 }}>
                      {a.sellGross > 0
                        ? <><Private>{fmtEur(convert(a.sellGross), locale)}</Private>{` ${symbol} ${t('tax_sold')}`}</>
                        : <><Private>{fmtEur(convert(a.swapGross || a.grossProceeds), locale)}</Private>{` ${symbol} ${t('tax_swapped')}`}</>}
                      {' · '}{a.eventsCount} {a.eventsCount === 1 ? t('tax_event') : t('tax_events')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginRight: 8 }}>
                    <div style={{
                      fontFamily: F.mono, fontSize: 14, fontWeight: 700,
                      color: Math.abs(a.realizedPnL) < 0.5 ? T.sub
                           : (a.realizedPnL >= 0 ? T.green : (T.loss || T.red)),
                    }}>
                      {a.realizedPnL >= 0 ? '+' : '−'}<Private>{fmt(convert(Math.abs(a.realizedPnL)), locale)}</Private>
                      <span style={{ fontSize: 10, opacity: 0.6 }}> {symbol}</span>
                    </div>
                    {a.hasCostBasisUnknown && (
                      <div style={{ fontSize: 11.5, color: T.gold, marginTop: 2 }}>
                        {t('tax_basis_incomplete')}
                      </div>
                    )}
                  </div>
                  <svg className="mo-chev" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.mute} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </div>
              </button>
            ))}
            {/* Gesamt — Spalte summiert sich sichtbar zur großen Zahl oben */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderTop: `2px solid ${T.line}` }}>
              <span style={{ flex: 1, fontFamily: F.display, fontSize: 14, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>{t('tax_total')}</span>
              <span style={{ textAlign: 'right', marginRight: 8, fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: realizedPnL >= 0 ? T.green : (T.loss || T.red) }}>
                {realizedPnL >= 0 ? '+' : '−'}<Private>{fmt(convert(Math.abs(realizedPnL)), locale)}</Private><span style={{ fontSize: 10, opacity: 0.6 }}> {symbol}</span>
              </span>
              <span style={{ width: 14 }} />
            </div>
          </div>
        </>
      )}

      {/* Export */}
      <div style={{ padding: '20px 18px 8px' }}>
        <RedButton
          variant="primary"
          size="lg"
          fullWidth
          icon={Icons.export}
          onClick={() => setShowExport(true)}
          disabled={exporting}
        >
          {exporting ? t('tax_generating') : t('tax_export_report')}
        </RedButton>
      </div>

      {exportError && (
        <div style={{ padding: '6px 18px 0', fontSize: 11, color: T.red, textAlign: 'center' }}>
          {exportError}
        </div>
      )}

      {!isPremium && !exporting && (
        <div style={{ padding: '8px 18px 0', fontSize: 11, color: T.mute, textAlign: 'center' }}>
          {t('tax_premium_hint')}
        </div>
      )}

      {showExport && (
        <ExportSheet
          open={showExport}
          onClose={() => setShowExport(false)}
          scope="year"
          availableYears={years}
          isPremium={isPremium}
          onPick={handleExportPick}
          onLocked={() => {
            setShowExport(false);
            onOpenPricing?.();
          }}
        />
      )}

      {/* Disclaimer */}
      <div style={{
        padding: '24px 22px 8px',
        fontSize: 11, lineHeight: 1.55,
        color: T.sub, fontFamily: F.family,
        textAlign: 'center',
      }}>
        {t('tax_disclaimer')}
      </div>
    </div>
  );
}

// Drilldown als EIGENE Ionic-Route (native App): rechnet den Report für EIN Asset
// und rendert die Detail-Ansicht mit nativem Swipe-back (deshalb hideBack → kein
// redundanter Back-Pfeil). Im Web/Desktop bleibt der In-Page-Drilldown mit Pfeil.
export function ReportAssetDrilldown({ sym, year, txs, marketData, onBack }) {
  const { T } = useTheme();
  const { country } = useProfile();
  const isDE = country === 'DE';
  const report = useMemo(
    () => buildReport({ txs, year, isDE, country, marketData }),
    [txs, year, isDE, country, marketData]
  );
  const heldAmount = useMemo(() => {
    const p = calcPortfolio(txs);
    return p[sym]?.menge || 0;
  }, [txs, sym]);
  const asset = report.perAsset.find(a => a.sym === sym);
  if (!asset) return null;
  return (
    <DrilldownView
      T={T}
      asset={{ ...asset, heldAmount }}
      year={year}
      isDE={isDE}
      marketData={marketData}
      onBack={onBack}
      hideBack
    />
  );
}

void R;
